import styled from '@emotion/styled';
import {CalendarApi} from '@fullcalendar/core';
import React, {
  createContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useTranslation} from 'react-i18next';
import {EHeaderButton} from 'webapp-lib/Components';
import {mixinFloatingSideBar} from 'webapp-lib/styling';

import {TransparentBackground} from '#/TransparentBackground';
import {Clock} from '_/assets/images';

import {Timetracker} from './Timetracker';

type TimeTrackerButtonProps = {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
};

export type CalendarContextProps = {
  calendarAPI: () => CalendarApi;
  setCalendarAPI: (api: CalendarApi) => void;
  timerInputRef: React.MutableRefObject<HTMLInputElement | null>;
};

export const CalendarAPIContext = createContext<CalendarContextProps>(null!);

export function TimetrackerButton({
  isOpen,
  setIsOpen,
}: TimeTrackerButtonProps): React.ReactElement {
  const {t} = useTranslation('timetracker');

  const [isOpenedOnce, setIsOpenedOnce] = useState(false);

  const toggleSideBar = () => {
    setIsOpen(!isOpen);
    setIsOpenedOnce(true);
  };

  return (
    <>
      <EHeaderButton
        isActive={isOpen}
        title={t('title')}
        onClick={toggleSideBar}
      >
        <Clock />
      </EHeaderButton>

      {isOpen ? (
        <TransparentBackground onMouseUp={() => setIsOpen(false)} />
      ) : null}
      <TimetrackerOverlay isOpen={isOpen}>
        {isOpenedOnce ? (
          <CalendarContext setFocus={isOpen}>
            <Timetracker isOpen={isOpen} />
          </CalendarContext>
        ) : null}
      </TimetrackerOverlay>
    </>
  );
}

function CalendarContext({
  setFocus,
  children,
}: {
  setFocus: boolean;
  children: React.ReactNode;
}): React.ReactElement {
  const [calendarApi, setCalendarApi] = useState<CalendarApi>(null!);
  const timerInputRef = useRef(null);

  const store: CalendarContextProps = useMemo(
    () => ({
      calendarAPI: () => calendarApi,
      setCalendarAPI: setCalendarApi,
      timerInputRef,
    }),
    [calendarApi]
  );

  useEffect(() => {
    if (setFocus) {
      store.timerInputRef.current?.focus();
    }
  }, [setFocus, store.timerInputRef]);

  return (
    <CalendarAPIContext.Provider value={store}>
      {children}
    </CalendarAPIContext.Provider>
  );
}

const TimetrackerOverlay = styled.div<{isOpen: boolean}>`
  ${mixinFloatingSideBar};

  width: var(--side-bar-wide-width);
  display: ${({isOpen}): string => (isOpen ? 'block' : 'none')};
`;
